import { useCallback, useEffect, useState } from 'react';
import { FAST_API_BASE_SKILLS } from '../config';

const normalizeSkillsResponse = rawSkills => {
  if (!Array.isArray(rawSkills)) {
    return [];
  }

  return rawSkills
    .map((skill, index) => {
      if (!skill || typeof skill !== 'object') {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[useSkillsData] Dropping malformed skill entry', { index, skill });
        }
        return null;
      }

      const skillid = typeof skill.skillid === 'number' ? skill.skillid : Number(skill.skillid);
      const skillname = typeof skill.skillname === 'string' ? skill.skillname.trim() : '';

      if (Number.isNaN(skillid) || skillid == null || skillname.length === 0) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[useSkillsData] Dropping skill missing required fields', {
            index,
            skillid,
            skillname
          });
        }
        return null;
      }

      const levels = Array.isArray(skill.levels) && skill.levels.length > 0 ? skill.levels : [1, 2, 3, 4, 5];

      return {
        skillid,
        skillname,
        type:
          typeof skill.skilltype === 'string'
            ? skill.skilltype.trim()
            : typeof skill.type === 'string'
              ? skill.type.trim()
              : null,
        levels
      };
    })
    .filter(Boolean);
};

export const useSkillsData = (accessToken) => {
  const [skills, setSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSkills = useCallback(async () => {
    if (!accessToken) {
      setSkills([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(FAST_API_BASE_SKILLS, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch skills');
      }

      const data = await response.json();
      setSkills(normalizeSkillsResponse(data?.skills));
    } catch (err) {
      console.error('Error fetching skills:', err);
      setSkills([]);
      setError(err.message || 'Failed to load skills');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  return {
    skills,
    isSkillsLoading: isLoading,
    skillsError: error,
    fetchSkills
  };
};

export default useSkillsData;
